import React from 'react'
import { FormikHelpers, FormikState } from 'formik'
import { Input, Radio } from 'antd'
import clsx from 'clsx'
import { AdminsFinanceCreateVendorRequest, VendorTypeEnum } from 'generated/api'
import Card from 'components/card/Card'
import CardHeaderForm from 'components/card/CardHeaderForm'
import ErrorHintForm from 'components/errors/ErrorHintForm'
import { validationPhoneNumber } from 'helpers/validation'
import { classNameInput } from 'helpers/formik/classNameForm'

interface FormBasicInformationProps {
  state: FormikState<AdminsFinanceCreateVendorRequest>
  helpers: FormikHelpers<AdminsFinanceCreateVendorRequest>
}

export default function FormBasicInformation(props: FormBasicInformationProps) {
  const {
    helpers: { setFieldTouched, setFieldValue },
    state,
  } = props

  const generateOnChangeHandler = (fieldName: string) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setFieldTouched(fieldName, true)
      setFieldValue(fieldName, value)
    }
  }

  const classNameInputType = (vendorType: VendorTypeEnum) => {
    return `xl:w-4/12 py-4 px-2 mr-0 rounded-lg border-solid border-2 border-[#E5E5E7] ${clsx({
      'bg-[#ECEEEB] text-[#3C5337]': state.values.type === vendorType,
      'bg-[#fff] text-[#323539]': state.values.type !== vendorType,
    })}`
  }

  const classNameInputName = classNameInput('name', state)
  const classNameInputOwnerName = classNameInput('ownerName', state)
  const classNameInputOwnerPhoneNo = classNameInput('ownerPhoneNo', state)
  const classNameInputPicName = classNameInput('picName', state)
  const classNameInputPicPhoneNo = classNameInput('picPhoneNo', state)
  const onChangeInputName = generateOnChangeHandler('name')
  const onChangeInputOwnerName = generateOnChangeHandler('ownerName')
  const onChangeInputPicName = generateOnChangeHandler('picName')

  return (
    <Card className="rounded-xl py-6 px-0" dataTestId="form-basic-information">
      <CardHeaderForm title="1. Informasi Dasar" />
      <Card.Body className="mt-0 px-6">
        <div className="flex flex-col gap-3 xl:gap-9">
          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">Nama Vendor</label>
            </div>
            <div className="xl:w-9/12">
              <Input
                data-testid="input-name"
                id="name"
                name="name"
                className={`xl:w-2/5 mb-1 w-full ${classNameInputName}`}
                placeholder="Contoh: Herman"
                value={state.values.name}
                onChange={e => onChangeInputName(e)}
                status={state.errors.name && state.touched.name ? 'error' : ''}
              />
              {state.errors.name && state.touched.name ? (
                <ErrorHintForm errorMessage={state.errors.name} dataTestId="error-message-name" />
              ) : null}
            </div>
          </div>

          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">Tipe Vendor</label>
            </div>
            <div className="xl:w-9/12">
              <Radio.Group
                name="type"
                className="flex gap-6"
                value={state.values.type}
                onChange={({ target: { value } }) => setFieldValue('type', value)}
              >
                <Radio
                  className={classNameInputType(VendorTypeEnum.FarmerStore)}
                  value={VendorTypeEnum.FarmerStore}
                >
                  <span className="font-medium text-base my-0 antialiased">Toko Tani</span>
                </Radio>
                <Radio
                  className={classNameInputType(VendorTypeEnum.GroceryStore)}
                  value={VendorTypeEnum.GroceryStore}
                >
                  <span className="font-medium text-base my-0 antialiased">Toko Sembako</span>
                </Radio>
                <Radio
                  className={classNameInputType(VendorTypeEnum.Company)}
                  value={VendorTypeEnum.Company}
                >
                  <span className="font-medium text-base my-0 antialiased">Perusahaan</span>
                </Radio>
              </Radio.Group>
              {state.errors.type && state.touched.type ? (
                <ErrorHintForm errorMessage={state.errors.type} dataTestId="error-message-type" />
              ) : null}
            </div>
          </div>
        </div>
        <hr className="my-6 border-[#E1E6EF] border-solid" />
        <div className="flex flex-col gap-3 xl:gap-9">
          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">Nama Pemilik</label>
            </div>
            <div className="xl:w-9/12">
              <Input
                data-testid="input-ownerName"
                id="ownerName"
                name="ownerName"
                className={`xl:w-2/5 mb-1 w-full ${classNameInputOwnerName}`}
                placeholder="Contoh: Sukimin"
                value={state.values.ownerName}
                onChange={e => onChangeInputOwnerName(e)}
                status={state.errors.ownerName && state.touched.ownerName ? 'error' : ''}
              />
              {state.errors.ownerName && state.touched.ownerName ? (
                <ErrorHintForm
                  errorMessage={state.errors.ownerName}
                  dataTestId="error-message-ownerName"
                />
              ) : null}
            </div>
          </div>
          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">
                Nomor Handphone Pemilik
              </label>
            </div>
            <div className="xl:w-9/12">
              <Input
                data-testid="input_ownerPhoneNo"
                id="ownerPhoneNo"
                name="ownerPhoneNo"
                className={`xl:w-2/5 mb-1 w-full ${classNameInputOwnerPhoneNo}`}
                value={state.values.ownerPhoneNo}
                onChange={e => {
                  const isPhoneNumber = validationPhoneNumber(e)
                  if (isPhoneNumber) setFieldValue('ownerPhoneNo', e.target.value)
                }}
                placeholder="Contoh: 08123456789"
                status={state.errors.ownerPhoneNo && state.touched.ownerPhoneNo ? 'error' : ''}
              />
              {state.errors.ownerPhoneNo && state.touched.ownerPhoneNo ? (
                <ErrorHintForm
                  errorMessage={state.errors.ownerPhoneNo}
                  dataTestId="error-message-ownerPhoneNo"
                />
              ) : null}
            </div>
          </div>
        </div>
        <hr className="my-6 border-[#E1E6EF] border-solid" />
        <div className="flex flex-col gap-3 xl:gap-9">
          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">Nama PIC</label>
            </div>
            <div className="xl:w-9/12">
              <Input
                data-testid="input-picName"
                id="picName"
                name="picName"
                className={`xl:w-2/5 mb-1 w-full ${classNameInputPicName}`}
                placeholder="Contoh: Agus"
                value={state.values.picName}
                onChange={e => onChangeInputPicName(e)}
                status={state.errors.picName && state.touched.picName ? 'error' : ''}
              />
              {state.errors.picName && state.touched.picName ? (
                <ErrorHintForm
                  errorMessage={state.errors.picName}
                  dataTestId="error-message-picName"
                />
              ) : null}
            </div>
          </div>
          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">
                Nomor Handphone PIC
              </label>
            </div>
            <div className="xl:w-9/12">
              <Input
                data-testid="input_picPhoneNo"
                id="picPhoneNo"
                name="picPhoneNo"
                className={`xl:w-2/5 mb-1 w-full ${classNameInputPicPhoneNo}`}
                placeholder="Contoh: 08123456789"
                onChange={e => {
                  const isPhoneNumber = validationPhoneNumber(e)
                  if (isPhoneNumber) setFieldValue('picPhoneNo', e.target.value)
                }}
                value={state.values.picPhoneNo}
                status={state.errors.picPhoneNo && state.touched.picPhoneNo ? 'error' : ''}
              />
              {state.errors.picPhoneNo && state.touched.picPhoneNo ? (
                <ErrorHintForm
                  errorMessage={state.errors.picPhoneNo}
                  dataTestId="error-message-picPhoneNo"
                />
              ) : null}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
